import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const [email, setEmail] = useState("admin@kikicentre.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (localStorage.getItem("kiki_admin_token")) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kiki_admin_token", data.access_token);
      localStorage.setItem("kiki_admin_user", JSON.stringify(data.user));
      nav("/admin");
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="inline-flex items-center bg-[#F5F5F0] px-4 py-2 rounded-lg self-start">
          <img src="/kiki-logo.png" alt="KIKI Centre for Technology" className="h-9 w-auto select-none" draggable={false} />
        </div>
        <div>
          <div className="label-overline text-[#FFCC00] mb-6">Admin · Session 2026</div>
          <h1 className="font-sans font-black text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Every <span className="font-display italic font-normal text-[#FFCC00]">enquiry</span><br />
            lives here.
          </h1>
          <p className="mt-6 max-w-md text-white/70 leading-relaxed">
            Track OTP-verified enquiries, newsletter subscribers, and website messages — all in one panel.
          </p>
        </div>
        <div className="label-overline text-white/40">Restricted access · Auth required</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} data-testid={ADMIN.loginForm} className="w-full max-w-sm space-y-6">
          <div>
            <div className="label-overline text-muted-foreground mb-3">Admin Panel</div>
            <h2 className="font-sans font-black text-4xl md:text-5xl leading-[1] tracking-tight">Sign in.</h2>
          </div>

          <label className="block">
            <span className="label-overline text-muted-foreground block mb-2">Email</span>
            <input data-testid={ADMIN.loginEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 bg-transparent border border-border focus:border-foreground focus:outline-none" />
          </label>
          <label className="block">
            <span className="label-overline text-muted-foreground block mb-2">Password</span>
            <input data-testid={ADMIN.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 bg-transparent border border-border focus:border-foreground focus:outline-none" />
          </label>

          {error && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} data-testid={ADMIN.loginError} className="text-sm text-destructive font-mono">
              {error}
            </motion.div>
          )}

          <button data-testid={ADMIN.loginSubmit} type="submit" disabled={loading} className="btn-pill-yellow w-full py-4">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Sign in <ArrowRight className="w-4 h-4" strokeWidth={2} /></>)}
          </button>

          <a href="/" className="label-overline text-muted-foreground hover:text-foreground link-sweep block text-center">← Back to site</a>
        </form>
      </div>
    </div>
  );
}
